import {
  wallOpeningDepth,
  wallOpeningHasExteriorSide,
  wallOpeningPlane,
  wallOpeningSide,
} from "building-common/openings/openingQuery";
import { ensurePolyLine2HasVerticalEnds } from "building-common/profile2Utils";
import { profile2OrientCopy } from "building-common/profile2Utils";
import { getActiveProfileDescriptorOrUndefined } from "building-generation/elements/profileTools";
import { getWallPath } from "building-generation/elements/walls/wall";
import type { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import { DoorOperationType } from "building-types/doors/doorOperationTypes";
import type { DoorUnit, GeneratedDoorPlacement, SwingSide } from "building-types/doors/doorTypes";
import type { OpeningSideName } from "building-types/openings/openingTypes";
import { BACK, FRONT } from "building-types/openings/openingTypes";
import type { BoardDescriptor, CustomDescriptor } from "building-types/profiles/profileTypes";
import { ProfileExtrusionType, ProfileExtrusionDescriptorType } from "building-types/profiles/profileTypesEnum";
import type { Prop } from "building-types/props/propTypes";
import type { ExteriorDoorCasingSettings } from "building-types/settings/exteriorDoorCasingSettings";
import type { HeadCasingSettings } from "building-types/settings/headCasingSettings";
import type { InteriorDoorCasingSettings } from "building-types/settings/interiorDoorCasingSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import { HeadCasingType } from "building-types/settings/settingsTypesEnum";
import type { ExteriorDoorCasingViewNoteTable } from "building-types/settings/viewNotesSettings";
import { ExteriorDoorCasingViewNoteType } from "building-types/settings/viewNotesSettings";
import { ViewEntityType } from "building-types/views/viewEntityType";
import type { ViewEntityDescriptor } from "building-types/views/viewEntityTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallIndex } from "building-types/walls/wallIndex";
import { WALL_THICKNESS_INTERIOR } from "common/constants/buildingObjects";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import type { MergedDoorSettings } from "doc/elements/doors/mergedDoorSettings";
import { roomIsExterior } from "doc/rooms/roomQuery";
import { defaultExteriorDoorCasingSettings } from "doc/settings/defaultSettings";
import { DOOR_UNIT_TRIM_CROSS_SECTION } from "doc/utils/doorTools";
import { exteriorMaterialOverrideNew, interiorMaterialOverrideNew } from "doc/utils/materialTools";
import { viewEntityDescriptorNew } from "doc/utils/viewEntityTools";
import type { BRep, Extrusion } from "geom/brep";
import { faceNew, faceOrNullMove } from "geom/brep";
import { tagFaces } from "geom/brep";
import { DIST_EPSILON, DIST_Z_FIGHT } from "geom/core";
import {
  extrudePolyLine2AlongNormal,
  extrudeFaceAlongPolyLine3,
  extrudeFace,
  extrusionToBRep,
  extrudeFaceAlongNormal,
} from "geom/extrude";
import { offsetPolyLine2 } from "geom/offset";
import { planarGraphAdjacentHalfEdge } from "geom/planarGraph";
import { planeNormal, planeMoveAlongNormal, planeCopy, planeReProjectPolyLine2, planeDistanceTo } from "geom/plane";
import {
  polyLine2Reverse,
  polyLine2Copy,
  polyLine2Edge,
  polyLine2RepairEdgeIndex,
  polyLine2ExtendEnd,
  polyLine2ExtendStart,
  polyLine2AABB2,
  polyLine2Extend,
  polyLine2SetCW,
  polyLine2SetCCW,
} from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import type { PolyLine3 } from "geom/polyLine3";
import { polyLine3ExtractEdge, polyLine3ExtendBothEnds } from "geom/polyLine3";
import {
  apronCasingEnabled,
  headCasingEnabled,
  sideCasingEnabled,
  headCasingRemoveCorner,
} from "../casing/casingQuery";
import { addHeadCasing } from "../headcasing";
import { offsetCasingShapes } from "../openings/offsetCasing";
import { sideOpposite } from "../openings/sideOpposite";
import { doorUnitNew } from "./../doors/door";
import { checkOrientationWithMode } from "./../streetOrientation";
import { doorDescriptorHasCasing } from "./doorDescriptorQuery";
import { doorShapes } from "./doorElevation";
import { doorBackOpening2, doorBackOpening3, doorFrontOpening2, doorFrontOpening3 } from "./doorQuery";
import { DOOR_JAMB_THICKNESS } from "./doorUtils";

export const DOOR_COMPONENT_APRON_CASING = "Apron_Casing";
export const DOOR_COMPONENT_GLAZING = "Glazing";
export const DOOR_COMPONENT_HEAD_CASING = "Head_Casing";
export const DOOR_COMPONENT_JAMB = "Jamb";
export const DOOR_COMPONENT_PANEL = "Panel";
export const DOOR_COMPONENT_SIDE_CASING = "Side_Casing";

function doorUnitAddTrim(
  unit: DoorUnit,
  side: OpeningSideName,
  streetOrientation: StreetOrientation,
  exteriorDoorCasing: ExteriorDoorCasingSettings | undefined,
  interiorDoorCasing: InteriorDoorCasingSettings | undefined,
  viewNotes: ExteriorDoorCasingViewNoteTable,
  profileSource: ActiveProfileSource | undefined = undefined,
) {
  const { doorPlacement: door } = unit;
  const { descriptor, opening } = door;
  if (!opening) {
    return;
  }

  if (!doorDescriptorHasCasing(descriptor)) {
    return;
  }

  let plane = wallOpeningPlane(opening, side);
  const oppositePlane = wallOpeningPlane(opening, sideOpposite(side));
  let oppositeOutline2: PolyLine2 | undefined;
  let oppositeOutline3: PolyLine3 | undefined;
  let isExterior: boolean;

  if (side === FRONT) {
    const room = opening.frontRoom;
    oppositeOutline2 = doorFrontOpening2(door, false);
    oppositeOutline3 = doorFrontOpening3(door);
    isExterior = roomIsExterior(room);
  } else {
    const room = opening.backRoom;
    oppositeOutline2 = doorBackOpening2(door, false);
    oppositeOutline3 = doorBackOpening3(door, false);
    isExterior = roomIsExterior(room);
  }

  const [nx, ny, nz] = planeNormal(plane);

  if (isExterior) {
    assertIsNotNullOrUndefined(exteriorDoorCasing);
    if (
      !exteriorDoorCasing.casingOrientation ||
      checkOrientationWithMode(streetOrientation, exteriorDoorCasing.casingOrientation)
    ) {
      let arcCurve = unit.arcCurve;
      let arcCurveSimple = arcCurve ? polyLine2Copy(arcCurve) : undefined;

      const {
        apronCasingType,
        apronDepth = 0,
        apronCasingMaterial,
        sideCasingType,
        sideCasingWidth = 0,
        sideCasingDepth = 0,
        sideCasingMaterial,
        headCasingType,
        casingHorizontalOffset = 0,
        casingLateralOffset = 0,
      } = exteriorDoorCasing;

      if (casingLateralOffset !== 0) {
        plane = planeMoveAlongNormal(planeCopy(plane), casingLateralOffset);
      }

      if (arcCurve) {
        arcCurve = planeReProjectPolyLine2(arcCurve, plane, opening.frontPlane);
        arcCurve = ensurePolyLine2HasVerticalEnds(arcCurve);
        if (side !== FRONT) {
          arcCurve = polyLine2Reverse(arcCurve);
        }
        assertIsNotNullOrUndefined(arcCurveSimple);
        arcCurveSimple = planeReProjectPolyLine2(arcCurveSimple, plane, opening.frontPlane);
        if (side !== FRONT) {
          arcCurveSimple = polyLine2Reverse(arcCurveSimple);
        }
      }

      if (casingHorizontalOffset !== 0) {
        assertIsNotNullOrUndefined(oppositeOutline2);
        let casingOutline = polyLine2Copy(oppositeOutline2);
        casingOutline = polyLine2SetCW(casingOutline);

        ({ casingOutline, arcCurve, arcCurveSimple } = offsetCasingShapes(casingHorizontalOffset, {
          casingOutline,
          arcCurve,
          arcCurveSimple,
        }));

        casingOutline = polyLine2SetCCW(casingOutline);
        oppositeOutline2 = casingOutline;
      }

      const trimExtend = sideCasingWidth;

      const apronCasingDescriptor = viewEntityDescriptorNew(
        uid(),
        ViewEntityType.APRON_CASING,
        viewNotes[ExteriorDoorCasingViewNoteType.APRON],
      );

      const sideCasingDescriptor = viewEntityDescriptorNew(
        uid(),
        ViewEntityType.SIDE_CASING,
        viewNotes[ExteriorDoorCasingViewNoteType.SIDE],
      );

      if (apronCasingEnabled(apronCasingType)) {
        const apronCasingMat = apronCasingMaterial ?? "trimExterior0";
        const apronBase = opening?.back?.apronCasing;
        if (apronBase) {
          const apron = extrudePolyLine2AlongNormal(apronBase, plane, apronDepth);
          unit.exteriorFront.add(
            apron,
            exteriorMaterialOverrideNew(
              apronCasingMat,
              undefined,
              apronCasingDescriptor,
              undefined,
              DOOR_COMPONENT_APRON_CASING,
            ),
          );
        }
      }

      if (doorDescriptorHasCasing(descriptor) && sideCasingEnabled(sideCasingType)) {
        const sideCasingMat = sideCasingMaterial ?? "trimExterior0";
        const mat = exteriorMaterialOverrideNew(
          sideCasingMat,
          undefined,
          sideCasingDescriptor,
          undefined,
          DOOR_COMPONENT_SIDE_CASING,
        );

        for (const profile of wallOpeningSide(opening, side).sideCasing) {
          const sideCasing = extrudePolyLine2AlongNormal(profile[0], plane, sideCasingDepth);
          unit.exteriorFront.add(sideCasing, mat);
        }
      }

      if (headCasingEnabled(headCasingType)) {
        assertIsNotNullOrUndefined(oppositeOutline2);
        const headcasingLine = polyLine2Edge(
          profile2OrientCopy(oppositeOutline2),
          polyLine2RepairEdgeIndex(oppositeOutline2, 2),
        );
        const apronLine = polyLine2Reverse(
          polyLine2Edge(profile2OrientCopy(oppositeOutline2), polyLine2RepairEdgeIndex(oppositeOutline2, 0)),
        );
        const extendedHeadcasingLine = polyLine2Copy(headcasingLine);

        if (sideCasingEnabled(sideCasingType)) {
          polyLine2ExtendEnd(extendedHeadcasingLine, trimExtend);
          polyLine2ExtendStart(extendedHeadcasingLine, trimExtend);
        }

        const profiles = opening.back;

        const { originX: bx, originY: by, originZ: bz } = plane;
        const wallWidth = planeDistanceTo(oppositePlane, bx, by, bz);

        const headCasingSettings = exteriorDoorCasing as HeadCasingSettings;

        addHeadCasing({
          profiles: profiles,
          bottomLine: polyLine2Reverse(extendedHeadcasingLine),
          apronLine,
          arch: headCasingRemoveCorner(headCasingSettings) ? arcCurveSimple : arcCurve,
          casingPlane: plane,
          openingPlane: plane,
          wallWidth,
          settings: headCasingSettings,
          extrusions: unit.decorations,
          viewNotes: viewNotes,
          exportComponentName: DOOR_COMPONENT_HEAD_CASING,
        });

        Object.assign(unit, { headCasingHeight: profiles.headHeight });
      }
    }
  } else {
    assertIsNotNullOrUndefined(oppositeOutline3);
    const path = oppositeOutline3.slice(3);
    //assumption that the input door is always rectangular
    const sideCasingLine1 = polyLine3ExtractEdge(path, 0);
    const sideCasingLine2 = polyLine3ExtractEdge(path, 2);
    const headCasingLine = polyLine3ExtractEdge(path, 1);
    let sideBoard: PolyLine2 | undefined = DOOR_UNIT_TRIM_CROSS_SECTION;
    let headBoard: PolyLine2 | undefined = DOOR_UNIT_TRIM_CROSS_SECTION;
    let trimSideCasing1: BRep | undefined, trimSideCasing2: BRep | undefined, trimHeaderCasing: BRep | undefined;
    let sideCasingWidth: number = 0,
      headCasingExtension: number = 0;

    if (interiorDoorCasing && profileSource) {
      if (interiorDoorCasing.sideTrimType === ProfileExtrusionType.NONE) {
        sideBoard = undefined;
      } else if (interiorDoorCasing.sideTrimType !== undefined) {
        const sideDescriptor = getActiveProfileDescriptorOrUndefined(profileSource, interiorDoorCasing.sideTrimType);
        if (sideDescriptor?.profileType === ProfileExtrusionDescriptorType.BOARD) {
          const { width, height } = sideDescriptor as BoardDescriptor;
          sideBoard = polyLine2New([0, 0, 0, width, height, width, height, 0, 0, 0]);
        } else if (sideDescriptor?.profileType === ProfileExtrusionDescriptorType.CUSTOM) {
          const { polyline } = sideDescriptor as CustomDescriptor;
          sideBoard = polyLine2New(polyline);
        }
      }

      if (sideBoard) {
        trimSideCasing1 = extrudeFaceAlongPolyLine3([sideBoard], sideCasingLine1, nx, ny, nz);
        trimSideCasing2 = extrudeFaceAlongPolyLine3([sideBoard], sideCasingLine2, nx, ny, nz);

        const { minX, maxX } = polyLine2AABB2(sideBoard);
        sideCasingWidth = maxX - minX;

        const sideCasingMaterial = interiorMaterialOverrideNew(
          interiorDoorCasing.sideMaterial,
          undefined,
          undefined,
          DOOR_COMPONENT_SIDE_CASING,
        );

        if (side === FRONT) {
          unit.interiorFront.add(trimSideCasing1, sideCasingMaterial);
          unit.interiorFront.add(trimSideCasing2, sideCasingMaterial);
        } else {
          unit.interiorBack.add(trimSideCasing1, sideCasingMaterial);
          unit.interiorBack.add(trimSideCasing2, sideCasingMaterial);
        }
      }

      headCasingExtension = interiorDoorCasing.headExtension + sideCasingWidth;
      polyLine3ExtendBothEnds(headCasingLine, headCasingExtension, headCasingExtension);

      //Head casing calculation
      if (interiorDoorCasing.headTrimType === ProfileExtrusionType.NONE) {
        headBoard = undefined;
      } else if (interiorDoorCasing.headTrimType !== undefined) {
        const headDescriptor = getActiveProfileDescriptorOrUndefined(profileSource, interiorDoorCasing.headTrimType);
        if (headDescriptor?.profileType === ProfileExtrusionDescriptorType.BOARD) {
          const { width, height } = headDescriptor as BoardDescriptor;
          headBoard = polyLine2New([0, 0, 0, width, height, width, height, 0, 0, 0]);
        } else if (headDescriptor?.profileType === ProfileExtrusionDescriptorType.CUSTOM) {
          const { polyline } = headDescriptor as CustomDescriptor;
          headBoard = polyLine2New(polyline);
        }
      }

      if (headBoard) {
        trimHeaderCasing = extrudeFaceAlongPolyLine3([headBoard], headCasingLine, nx, ny, nz);
        const headCasingMaterial = interiorMaterialOverrideNew(
          interiorDoorCasing.headMaterial,
          undefined,
          undefined,
          DOOR_COMPONENT_HEAD_CASING,
        );

        if (side === FRONT) {
          unit.interiorFront.add(trimHeaderCasing, headCasingMaterial);
        } else {
          unit.interiorBack.add(trimHeaderCasing, headCasingMaterial);
        }
      }
    } else {
      const trim = extrudeFaceAlongPolyLine3([sideBoard], path, nx, ny, nz);

      if (side === FRONT) {
        unit.interiorFront.add(trim, interiorMaterialOverrideNew("trimInterior0"));
      } else {
        unit.interiorBack.add(trim, interiorMaterialOverrideNew("trimInterior0"));
      }
    }
  }
}

/**
 * Creates a 0 thickness door jamb. Users do not have control of this yet
 */
function doorUnitAddJamb(unit: DoorUnit, floorElevationOffset = 0) {
  const { opening } = unit.doorPlacement;
  if (!opening) {
    return;
  }

  const jambDepth = wallOpeningDepth(opening);
  if (jambDepth < DIST_EPSILON) {
    return;
  }

  const { backPlane, back: backProfile } = opening;

  /**
   * Door Units do not accurately add a rough opening into walls and foundations.
   * Rather than thickening the jamb similar to window units,
   * we need to offset the jamb to account for this and prevent z-fighting
   */
  const jambThicknessTolerance = DIST_Z_FIGHT; // TODO - this can possibly be removed
  const jambDepthTolerance = DIST_Z_FIGHT / 2;
  const jambPlane = planeMoveAlongNormal(planeCopy(backPlane), -jambDepthTolerance);

  const jambProfile = polyLine2SetCCW(polyLine2Copy(backProfile.opening2)).slice(2);
  const jambProfileWithFloorElevation = polyLine2Extend(jambProfile, floorElevationOffset, floorElevationOffset);

  const jambProfileInnerOffset = offsetPolyLine2(jambProfileWithFloorElevation, -jambThicknessTolerance);
  const jambProfileOuterOffset = offsetPolyLine2(jambProfileWithFloorElevation, DOOR_JAMB_THICKNESS);

  const jambFrameOutline = polyLine2New([
    ...jambProfileInnerOffset,
    ...polyLine2Reverse(jambProfileOuterOffset),
    jambProfileInnerOffset[0],
    jambProfileInnerOffset[1],
  ]);

  const jambFrame = faceNew([jambFrameOutline], jambPlane);
  const jambExtrusion = extrudeFaceAlongNormal(jambFrame, -(jambDepth - jambDepthTolerance * 2));

  /**
   * @TODO - users probably expect to:
   * - see the trim color on either side of the window be reflected by the interior/exterior nature of each side
   * - be able to override the trim material on either side.
   */
  const isExterior = wallOpeningHasExteriorSide(opening);
  if (isExterior) {
    unit.exteriorBack.add(
      jambExtrusion,
      exteriorMaterialOverrideNew("trimExterior0", undefined, undefined, undefined, DOOR_COMPONENT_JAMB),
    );
  } else {
    unit.interiorBack.add(
      jambExtrusion,
      interiorMaterialOverrideNew("trimInterior0", undefined, undefined, DOOR_COMPONENT_JAMB),
    );
  }
}

interface DoorUnitWithPropsParams {
  door: GeneratedDoorPlacement;
  swingSide: SwingSide;
  prop?: Prop;
  streetOrientation: StreetOrientation;
  mergedDoorSettings: MergedDoorSettings;
  profileSource: ActiveProfileSource;
}

export function doorUnitWithProp({
  door,
  swingSide,
  prop,
  streetOrientation,
  profileSource,
  mergedDoorSettings: { exteriorCasing, interiorCasing, viewNoteTable },
}: DoorUnitWithPropsParams): DoorUnit {
  door.computedSwingSide = swingSide;
  const { includeTrim } = door.descriptor;

  const unit = doorUnitNew(door, streetOrientation, swingSide, prop, exteriorCasing, interiorCasing, viewNoteTable);

  if (door.opening?.hasJamb) {
    doorUnitAddJamb(unit);
  }

  if (includeTrim) {
    doorUnitAddTrim(unit, FRONT, streetOrientation, exteriorCasing, interiorCasing, viewNoteTable, profileSource);
    doorUnitAddTrim(unit, BACK, streetOrientation, exteriorCasing, interiorCasing, viewNoteTable, profileSource);
  }
  return unit;
}

interface DoorUnitSimplePanelParams {
  door: GeneratedDoorPlacement;
  swingSide: SwingSide;
  panelThickness?: number;
  panelOffsetDepth?: number;
  prop?: Prop;
  streetOrientation: StreetOrientation;
  mergedDoorSettings: MergedDoorSettings;
  viewEntityDescriptor: ViewEntityDescriptor;
  profileSource: ActiveProfileSource;
}

export function doorUnitSimplePanel({
  door,
  swingSide,
  panelThickness = 0.25,
  panelOffsetDepth = 2,
  prop,
  streetOrientation,
  mergedDoorSettings: { exteriorCasing, interiorCasing, viewNoteTable },
  viewEntityDescriptor,
  profileSource,
}: DoorUnitSimplePanelParams): DoorUnit {
  door.computedSwingSide = swingSide;

  const unit = doorUnitNew(door, streetOrientation, swingSide, prop, exteriorCasing, interiorCasing, viewNoteTable);
  assertIsNotNullOrUndefined(door.opening);
  const frontPlane = planeCopy(door.opening.frontPlane);
  const [nx, ny, nz] = planeNormal(frontPlane);

  const { front } = door.opening;

  const face = faceNew([polyLine2SetCW(front.opening2)], frontPlane);
  faceOrNullMove(
    face,
    -nx * (panelOffsetDepth + door.lateralOffset - panelThickness),
    -ny * (panelOffsetDepth + door.lateralOffset - panelThickness),
    -nz,
  );

  const brep = extrudeFace(face, -nx * panelThickness, -ny * panelThickness, -nz);

  /**
   * if door is open we don't want to generate any exterior or interior front faces
   * @TODO - this check almost certainly doesn't catch all cases where this door is open
   */
  const doorIsOpen: boolean = door.descriptor.operationType === DoorOperationType.OPEN;
  if (!doorIsOpen) {
    if (door.descriptor.isExterior) {
      unit.exteriorFront.add(
        brep,
        exteriorMaterialOverrideNew("doorExterior0", undefined, viewEntityDescriptor, undefined, DOOR_COMPONENT_PANEL),
      );
    } else {
      unit.interiorFront.add(
        brep,
        interiorMaterialOverrideNew("doorInterior0", undefined, undefined, DOOR_COMPONENT_PANEL),
      );
    }
  }

  if (door.opening?.hasJamb) {
    doorUnitAddJamb(unit);
  }

  doorUnitAddTrim(unit, FRONT, streetOrientation, exteriorCasing, interiorCasing, viewNoteTable, profileSource);
  doorUnitAddTrim(unit, BACK, streetOrientation, exteriorCasing, interiorCasing, viewNoteTable, profileSource);

  return unit;
}

interface DoorUnitArcParams {
  door: GeneratedDoorPlacement;
  streetOrientation: StreetOrientation;
  wallIndex: WallIndex;
  swingSide: SwingSide;
  mergedDoorSettings: MergedDoorSettings;
  wallThickness: number;
  panelOffsetDepth?: number;
  segmentedArch?: boolean;
  arcFactor?: number;
  prop?: Prop;
}

export function doorUnitArc({
  door,
  streetOrientation,
  wallIndex,
  swingSide,
  wallThickness,
  panelOffsetDepth = 2,
  prop,
  mergedDoorSettings,
}: DoorUnitArcParams): DoorUnit {
  // @TODO(Andrew): replace this (and all instances of door.opening) with a lazy getter
  if (!door.opening) {
    throw new Error("doorUnitArc: door must have an opening");
  }

  const { front: frontProfile } = door.opening;
  const frontShapes = doorShapes(door.descriptor, frontProfile.openingRect2);

  door.computedSwingSide = swingSide;

  const { exteriorCasing = defaultExteriorDoorCasingSettings, interiorCasing, viewNoteTable } = mergedDoorSettings;

  const { frontPlane } = door.opening;
  const unit = doorUnitNew(door, streetOrientation, swingSide, prop, exteriorCasing, interiorCasing, viewNoteTable);
  const holePlane = planeCopy(frontPlane);
  const [nx, ny, nz] = planeNormal(holePlane);

  const face = faceNew([frontProfile.opening2], holePlane);
  faceOrNullMove(face, -nx * panelOffsetDepth, -ny * panelOffsetDepth, -nz);

  const wallInt = WALL_THICKNESS_INTERIOR / 2;
  const wallExt = wallThickness - wallInt;

  door.springHeight = frontShapes.arcHeight !== undefined ? door.descriptor.height - frontShapes.arcHeight : undefined;

  let headCasingArcCurve: PolyLine2 | undefined = undefined;

  if ((frontShapes.arcFactor !== undefined && frontShapes.arcFactor > DIST_EPSILON) || frontShapes.arcHeight !== 0) {
    const arcFace = frontShapes.arcOutline ? faceNew([frontShapes.arcOutline], holePlane) : null;
    faceOrNullMove(arcFace, nx * 0.25, ny * 0.25, nz);

    const addWallExtrusion = (extrusion: Extrusion, isFront: boolean) => {
      const { halfEdgeToWall, walls } = wallIndex;
      const doorHalfEdgeIndex = isFront ? door.halfEdgeIndex : planarGraphAdjacentHalfEdge(door.halfEdgeIndex);
      const wallId = halfEdgeToWall[doorHalfEdgeIndex];

      if (!wallId) {
        return;
      }

      const wall = walls[wallId];

      if (!wall || wall.wallType !== WallType.SOLID) {
        return;
      }

      const { wallFaces } = wall;
      const path = getWallPath(wall);
      const { leftFaces, rightFaces } = wallFaces;
      const { halfEdgeIndex } = path;
      const isLeft = halfEdgeIndex !== doorHalfEdgeIndex;
      const sideFaces = isLeft ? leftFaces : rightFaces;

      const tags = [
        { type: "PARENT", parentId: door.id, parentType: door.type },
        { type: "CONDITION", condition: "DOOR_UNIT_ARC" },
      ];

      sideFaces.push(...tagFaces(extrusionToBRep(extrusion).faces, tags));
    };

    if (door.descriptor.isExterior) {
      assertIsNotNullOrUndefined(arcFace);
      const extrusion1 = extrudeFace(arcFace, nx * -wallExt, ny * -wallExt, nz);
      const extrusion2 = extrudeFace(arcFace, nx * wallInt, ny * wallInt, nz);
      addWallExtrusion(extrusion1, true);
      addWallExtrusion(extrusion2, false);
    } else {
      assertIsNotNullOrUndefined(arcFace);
      const extrusion1 = extrudeFace(arcFace, nx * wallInt, ny * wallInt, nz);
      const extrusion2 = extrudeFace(arcFace, nx * -wallInt, ny * -wallInt, nz);
      addWallExtrusion(extrusion1, false);
      addWallExtrusion(extrusion2, true);
    }

    Object.assign(unit, { arcCurve: frontShapes.arcCurve });

    assertIsNotNullOrUndefined(frontShapes.arcCurve);
    headCasingArcCurve = polyLine2Reverse(polyLine2Copy(frontShapes.arcCurve));

    if (exteriorCasing.headCasingType === HeadCasingType.TRIM) {
      headCasingArcCurve = planeReProjectPolyLine2(headCasingArcCurve, door.opening.frontPlane, door.opening.backPlane);
    }
  }

  if (unit.doorPlacement.descriptor.includeTrim) {
    doorUnitAddTrim(unit, FRONT, streetOrientation, exteriorCasing, interiorCasing, viewNoteTable);
    doorUnitAddTrim(unit, BACK, streetOrientation, exteriorCasing, interiorCasing, viewNoteTable);
  }

  return unit;
}
